/**
 * Academy domain — `AcademyRealtimeStore` state shape. A thin, in-memory mirror of
 * the live hub streams (FR-030). Buffers are keyed by `liveEventId`.
 */
import type { Guid, LiveEventStatusType, LiveQuestionStatusType } from './academy-enums.model';
import type { ReactionCounts } from './academy-responses.model';
import type {
  ChatMessageAddedEvent,
  HubRejoin,
  HubSendRejection,
  HubStatus,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  QuestionAddedEvent,
  ReactionBroadcastEvent,
} from './academy-realtime.model';

/**
 * Latest hub moderation/vote values for one question, kept apart from the hub buffer so
 * they also reach questions loaded over REST (which never get a `QuestionAdded`).
 */
export interface HubQuestionPatch {
  upvoteCount?: number;
  status?: LiveQuestionStatusType;
}

/** The hub's latest word on whether one person is muted, and the position it arrived in. */
export interface HubMuteChange {
  isMuted: boolean;
  order: number;
}

export interface AcademyRealtimeState {
  /** Connection lifecycle. */
  status: HubStatus;
  /** The event currently joined (null when not in a live room). */
  connectedEventId: Guid | null;
  /** Set when the last join was rejected by the audience gate. */
  joinRejected: boolean;
  /**
   * Set when a join the connection cut short did not go through — the first one or a re-join
   * after a reconnect. Distinct from `joinRejected` because it is transient (the connection
   * dropped mid-invoke) and the next reconnect clears it: it never drives the audience banner,
   * and it never clears `connectedEventId` — doing so silenced the page for good once the
   * re-join recovered.
   */
  rejoinRejected: boolean;
  /** Latest known status per event (from `EventStatusChanged`). */
  eventStatusById: Record<Guid, LiveEventStatusType>;
  /** Live viewer count per event. */
  viewerCountByEvent: Record<Guid, number>;
  /** Q&A buffer per event (added, then upvote/status-patched in place). */
  questionsByEvent: Record<Guid, QuestionAddedEvent[]>;
  /** Chat buffer per event (added, removed in place). */
  chatByEvent: Record<Guid, ChatMessageAddedEvent[]>;
  /** Aggregated reaction counts per event. */
  reactionCountsByEvent: Record<Guid, ReactionCounts>;
  /** Most recent individual reaction (for transient float animations). */
  lastReaction: ReactionBroadcastEvent | null;
  /**
   * Producer roster events per event, keyed by `liveAttendanceId`: the latest event for a
   * session replaces the earlier one (a `ParticipantLeft` supersedes its `ParticipantJoined`).
   */
  participantsByEvent: Record<Guid, Record<Guid, ParticipantJoinedEvent | ParticipantLeftEvent>>;
  /**
   * Every `QuestionUpvoted` / `QuestionStatusChanged` per event, keyed by question id (latest
   * value wins). Applied last over the REST backlogs, whose rows the hub buffer cannot patch.
   * Entries are dropped when the studio's producer re-read **lands** (never at the re-join
   * itself), and only for what that read supersedes — see `patchesAtProducerRejoinByEvent`.
   */
  questionPatchesByEvent: Record<Guid, Record<Guid, HubQuestionPatch>>;
  /**
   * Snapshot of an event's `questionPatchesByEvent` entry taken when a producer re-join went
   * out — which is when the studio's re-read is issued. It is the reference the drop compares
   * against once that read lands: a value recorded *after* the re-join is not in the read
   * (the read's snapshot is older) and must survive it.
   *
   * Kept out of `questionPatchesByEvent` instead of dropping it there so that, while the
   * re-read is in flight — or forever, if it fails — the panel keeps showing what it had.
   */
  patchesAtProducerRejoinByEvent: Record<Guid, Record<Guid, HubQuestionPatch>>;
  /**
   * Every `ChatMessageRemoved` id per event. Filters REST history rows too: removing a
   * message from the hub buffer alone would let its REST copy resurface. Kept across a
   * producer re-join: a removal is never undone.
   */
  removedChatIdsByEvent: Record<Guid, Guid[]>;
  /**
   * Every `ChatParticipantMuteChanged` per event, keyed by person id (latest value wins), each
   * with the position it arrived in. Applied over the studio's muted list
   * (`AcademyQnaChatStore.mutedByEvent`), and dropped when a fresh read of that list lands —
   * only the changes that had already arrived when the read went out, which the read carries.
   */
  muteChangesByEvent: Record<Guid, Record<Guid, HubMuteChange>>;
  /** How many `ChatParticipantMuteChanged` were recorded so far — the position of the last one. */
  muteChangeCount: number;
  /**
   * The last group re-joined after a reconnect (a new object per re-join). Hub events sent
   * while the connection was down never arrive, so a page reacts by re-reading its backlog.
   * Published in the same write that snapshots the producer re-join's recorded values.
   */
  lastRejoin: HubRejoin | null;
  /**
   * The last chat message that did not go out, for the page to tell whoever wrote it. A new
   * object each time, so two in a row are two announcements. Reactions are not held: one
   * that is dropped is not worth interrupting anyone for.
   */
  chatSendRejection: HubSendRejection | null;
}

export const initialAcademyRealtimeState: AcademyRealtimeState = {
  status: 'disconnected',
  connectedEventId: null,
  joinRejected: false,
  rejoinRejected: false,
  eventStatusById: {},
  viewerCountByEvent: {},
  questionsByEvent: {},
  chatByEvent: {},
  reactionCountsByEvent: {},
  lastReaction: null,
  participantsByEvent: {},
  questionPatchesByEvent: {},
  patchesAtProducerRejoinByEvent: {},
  removedChatIdsByEvent: {},
  muteChangesByEvent: {},
  muteChangeCount: 0,
  lastRejoin: null,
  chatSendRejection: null,
};
